/**
 * Who may see which leave request, and how much of it.
 *
 * One module owns this so the shared calendar, the team matrix and any future view cannot
 * drift apart on a privacy rule. Before it existed the Apply Leave calendar showed every
 * request to everybody, including rejected and cancelled ones — which both overstated how
 * many people were away and exposed decisions that are nobody else's business.
 *
 * The rules, as specified:
 *   - Shared calendar: APPROVED only, and then visible to everyone. A request appears
 *     the moment the manager approves it and not before, so the calendar always reads
 *     as confirmed absences rather than intentions.
 *   - PENDING / REJECTED / CANCELLED → never on the shared calendar. They stay where
 *     they are actionable: the applicant's own request list and the approvals queue.
 *   - The reason text is private in every case (see canSeeLeaveReason).
 */

import { LeaveRequest, LeaveStatus } from '../types/leave';
import { UserRole } from '../types/user';
import { sameIdentity } from './identity';

/** Roles that oversee leave for the whole organisation. */
const ORG_WIDE_ROLES: string[] = [
  UserRole.HR_ADMIN,
  UserRole.SUPER_ADMIN,
  UserRole.FINANCE_ADMIN,
];

export interface LeaveViewer {
  email?: string;
  role?: string;
  /** Emails of this viewer's direct reports, when they manage anyone. */
  reportEmails?: string[];
}

export const isOrgWideViewer = (viewer: LeaveViewer): boolean =>
  !!viewer.role && ORG_WIDE_ROLES.includes(viewer.role);

/**
 * Roles that may decide a leave request at all — the approver set.
 *
 * Centralised here because it was previously spelled out in three places that had drifted:
 * the sidebar's `isLeaveApprover` (Manager + HR + Super + Finance), the approvals page's
 * `ADMIN_ROLES` (HR + Super + Finance), and leaveShared's `isManagerRole` (Manager + HR +
 * Super, no Finance). The result was a role that could reach the approvals queue but was
 * treated as a non-approver by the Comp-Off and cancellation pages, so "who can approve"
 * depended on which screen you asked.
 */
const APPROVER_ROLES: string[] = [
  UserRole.MANAGER,
  ...ORG_WIDE_ROLES,
];

/** Whether this viewer can decide leave requests for anyone at all. */
export const isLeaveApprover = (viewer: LeaveViewer): boolean =>
  !!viewer.role && APPROVER_ROLES.includes(viewer.role);

/**
 * Whether this viewer may decide *this particular* request.
 *
 * The org-wide roles — HR Admin, Super Admin, Finance Admin — act on every employee; a
 * manager only on their own reports, which is why the directory lookup that populates
 * `reportEmails` doubles as the authorisation boundary.
 *
 * Self-approval is deliberately NOT blocked here, because today it is allowed: an org-wide
 * viewer's queue includes their own request. Most HR systems forbid it on separation-of-duties
 * grounds, but adding that rule would strand a lone HR Admin with no way to get their own
 * leave approved, so it is left as a policy decision rather than smuggled in as a bug fix.
 */
export const canDecideLeave = (leave: LeaveRequest, viewer: LeaveViewer): boolean => {
  if (!isLeaveApprover(viewer)) return false;
  return isOrgWideViewer(viewer) || isReportsLeave(leave, viewer);
};

export const isOwnLeave = (leave: LeaveRequest, viewer: LeaveViewer): boolean =>
  !!viewer.email && sameIdentity(leave.employeeEmail, viewer.email);

/**
 * Whether this leave belongs to someone reporting to the viewer.
 *
 * Domain-aware, because a report's leave may be filed under either of their two email
 * addresses while the directory lists only one.
 */
export const isReportsLeave = (leave: LeaveRequest, viewer: LeaveViewer): boolean =>
  (viewer.reportEmails || []).some(email => sameIdentity(email, leave.employeeEmail));

/**
 * Whether the viewer may see this request on a shared calendar at all.
 *
 * The shared calendar shows every request that is **live** — one somebody has made and
 * nobody has finished deciding — plus the approved ones. Three statuses qualify, and each
 * is on the calendar for a different reason:
 *
 *   APPROVED                 Booked. The person will be away.
 *   CANCELLATION_REQUESTED   Still booked, still consuming balance (see leaveBalanceService)
 *                            until a manager decides. The person is still going to be away if
 *                            the cancellation is refused, so hiding it the moment somebody
 *                            asked to cancel understated coverage for absences likely to
 *                            stand, and flickered the entry back on for every refusal.
 *   SUBMITTED                Not booked yet, and shown anyway: a team planning around a
 *                            calendar that hides every request awaiting approval plans around
 *                            a week that is about to change. The stage word on the entry is
 *                            what stops it being read as booked — see leaveStageLabel.
 *
 * REJECTED and CANCELLED stay off. They are leave that is settled and not happening, and
 * listing them would overstate absence with no decision left for anyone to make.
 *
 * Visibility is status-based, not role-based: a live request is public to the whole company,
 * exactly as an approved one is. That is a deliberate trade — a pending request is no longer
 * private to its applicant and their manager — and it is the reason the reason TEXT stays
 * restricted (see canSeeLeaveReason). This governs only the name/type/date entry.
 */
const CALENDAR_VISIBLE_STATUSES = new Set([
  String(LeaveStatus.APPROVED).toUpperCase(),
  String(LeaveStatus.CANCELLATION_REQUESTED).toUpperCase(),
  String(LeaveStatus.SUBMITTED).toUpperCase(),
]);

export const canSeeLeaveOnCalendar = (leave: LeaveRequest, _viewer: LeaveViewer): boolean =>
  CALENDAR_VISIBLE_STATUSES.has(String(leave.status).toUpperCase());

/**
 * Whether the reason text may be shown.
 *
 * Never for somebody else's leave, regardless of status — a reason is often medical or
 * personal, and "who is away" does not require knowing why. Managers and HR see it in the
 * approvals queue, which is a different, purposeful context.
 */
export const canSeeLeaveReason = (leave: LeaveRequest, viewer: LeaveViewer): boolean => {
  if (isOwnLeave(leave, viewer)) return true;
  if (isOrgWideViewer(viewer)) return true;
  return isReportsLeave(leave, viewer);
};

/** Filter a list down to what this viewer is allowed to see on a calendar. */
export const visibleLeaves = (leaves: LeaveRequest[], viewer: LeaveViewer): LeaveRequest[] =>
  leaves.filter(leave => canSeeLeaveOnCalendar(leave, viewer));

/** Add whole days to an ISO date, staying in local time so no timezone shift creeps in. */
const addDays = (iso: string, days: number): string => {
  const [year, month, day] = (iso || '').split('-').map(Number);
  if (!year || !month || !day) return iso;
  const date = new Date(year, month - 1, day + days);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * The first day on which an employee can no longer change their own request.
 *
 * Exclusive, so a lock period of 0 makes the leave's own start date the cutoff — editable up
 * to the day before, nothing on the day itself. Each extra day of lock period extends the
 * window past the start date by one.
 */
export const employeeChangeDeadline = (startDate: string, lockPeriodDays: number): string =>
  addDays(startDate, Math.max(0, Math.floor(lockPeriodDays || 0)));

/**
 * Whether the viewer may edit or delete this request, and why not when they cannot.
 *
 * Edit and delete share one window by design: both rewrite a request somebody may already
 * have acted on, so it makes no sense to permit one and not the other.
 *
 * The gate is purely about time and ownership. An employee keeps control of their own request
 * until the deadline — including an approved one, because plans change and withdrawing future
 * leave is normal; the manager and HR are notified so nothing is undone quietly. After the
 * deadline only HR and Super Admin can act.
 *
 * Managers are deliberately excluded even for their own reports: they approve or reject,
 * which leaves a decision on record, where an edit or deletion would leave nothing.
 */
export const canModifyLeave = (
  leave: LeaveRequest,
  viewer: LeaveViewer,
  today: string,
  lockPeriodDays: number
): { allowed: boolean; reason?: string } => {
  if (isOrgWideViewer(viewer)) return { allowed: true };

  if (!isOwnLeave(leave, viewer)) {
    return {
      allowed: false,
      reason: isReportsLeave(leave, viewer)
        ? 'Managers approve or reject requests rather than editing them, so the decision stays on record.'
        : 'You can only change your own leave requests.',
    };
  }

  const deadline = employeeChangeDeadline(leave.startDate, lockPeriodDays);
  if (today >= deadline) {
    return {
      allowed: false,
      reason: lockPeriodDays > 0
        ? `The ${lockPeriodDays}-day change window for this leave closed on ${deadline}. Contact HR to make changes.`
        : `This leave started on ${leave.startDate} and can no longer be changed. Contact HR if it needs correcting.`,
    };
  }

  return { allowed: true };
};

/**
 * Deletion permission. Identical to editing — kept as its own name so call sites read clearly.
 */
export const canDeleteLeave = canModifyLeave;
